import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, Settings, ArrowRight } from 'lucide-react';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

function fmt(n) {
  if (n < 0) return `-${fmt(-n)}`;
  if (n >= 1e9) return `₦${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `₦${(n / 1e6).toFixed(1)}M`;
  return `₦${Math.round(n).toLocaleString()}`;
}

function NumberInput({ value, onChange, label, sub, testId, prefix = '₦' }) {
  return (
    <div>
      {label && <label className="text-xs font-semibold text-[#374151] mb-1.5 block">{label}</label>}
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-[#9ca3af]">{prefix}</span>
        <input
          type="text"
          inputMode="numeric"
          value={value === 0 ? '' : value.toLocaleString()}
          onChange={e => {
            const raw = e.target.value.replace(/[^0-9]/g, '');
            onChange(raw === '' ? 0 : parseInt(raw, 10));
          }}
          className="w-full h-11 pl-8 pr-4 rounded-xl border border-[#e5e7eb] text-sm font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all"
          style={mono}
          data-testid={testId}
        />
      </div>
      {sub && <p className="text-[11px] text-[#9ca3af] mt-1">{sub}</p>}
    </div>
  );
}

function calcPAYE(annualChargeable) {
  if (annualChargeable <= 0) return 0;
  const bands = [
    { limit: 800000, rate: 0 },
    { limit: 2200000, rate: 0.15 },
    { limit: 9000000, rate: 0.18 },
    { limit: 13000000, rate: 0.21 },
    { limit: 25000000, rate: 0.23 },
    { limit: Infinity, rate: 0.25 },
  ];
  let remaining = annualChargeable;
  let tax = 0;
  for (const band of bands) {
    const taxable = Math.min(remaining, band.limit);
    tax += taxable * band.rate;
    remaining -= taxable;
    if (remaining <= 0) break;
  }
  return tax;
}

function computeScenario(totalPackage, benefits, opts) {
  const { basicPct, housingPct, transportPct, employerPensionRate, employeePensionRate, annualRent } = opts;
  const pctSum = basicPct + housingPct + transportPct;
  const totalBenefits = benefits.reduce((s, b) => s + b.amount, 0);
  const lifeInsurance = benefits.find(b => b.name === 'Life Insurance')?.amount || 0;

  // --- WITH PEBBLES ---
  // Employer pension on pensionable emoluments of CASH salary
  // Cash Salary = Total - Employer Pension - Benefits
  // Pensionable = pctSum × Cash Salary
  // Employer Pension = employerPensionRate × Pensionable
  // Cash = Total - Benefits - employerPensionRate × pctSum × Cash
  // Cash × (1 + employerPensionRate × pctSum) = Total - Benefits
  const cashWith = (totalPackage - totalBenefits) / (1 + employerPensionRate * pctSum);
  const pensionableWith = pctSum * cashWith;
  const employerPensionWith = employerPensionRate * pensionableWith;
  const employeePensionWith = employeePensionRate * pensionableWith;

  const annualCashWith = cashWith * 12;
  const rentRelief = Math.min(0.2 * annualRent, 500000);
  const lifeDeduction = lifeInsurance * 12;
  const chargeableWith = annualCashWith - employeePensionWith * 12 - rentRelief - lifeDeduction;
  const annualPAYEWith = calcPAYE(chargeableWith);
  const monthlyPAYEWith = annualPAYEWith / 12;
  const cashAfterTaxWith = cashWith - monthlyPAYEWith - employeePensionWith;

  // --- WITHOUT PEBBLES ---
  // Same total spend, ALL as cash (no benefits carved out)
  const cashWithout = (totalPackage) / (1 + employerPensionRate * pctSum);
  const pensionableWithout = pctSum * cashWithout;
  const employerPensionWithout = employerPensionRate * pensionableWithout;
  const employeePensionWithout = employeePensionRate * pensionableWithout;

  const annualCashWithout = cashWithout * 12;
  const chargeableWithout = annualCashWithout - employeePensionWithout * 12 - rentRelief;
  const annualPAYEWithout = calcPAYE(chargeableWithout);
  const monthlyPAYEWithout = annualPAYEWithout / 12;
  const cashAfterTaxWithout = cashWithout - monthlyPAYEWithout - employeePensionWithout;
  const leftWithout = cashAfterTaxWithout - totalBenefits;

  return {
    totalPackage,
    totalBenefits,
    // With
    cashWith: Math.round(cashWith),
    employerPensionWith: Math.round(employerPensionWith),
    employeePensionWith: Math.round(employeePensionWith),
    payeWith: Math.round(monthlyPAYEWith),
    cashAfterTaxWith: Math.round(cashAfterTaxWith),
    leftWith: Math.round(cashAfterTaxWith), // living expenses covered
    // Without
    cashWithout: Math.round(cashWithout),
    employerPensionWithout: Math.round(employerPensionWithout),
    employeePensionWithout: Math.round(employeePensionWithout),
    payeWithout: Math.round(monthlyPAYEWithout),
    cashAfterTaxWithout: Math.round(cashAfterTaxWithout),
    leftWithout: Math.round(leftWithout),
    // Delta
    delta: Math.round(cashAfterTaxWith) - Math.round(leftWithout),
  };
}

export default function EmployerCalculator({ openDemo }) {
  const [mode, setMode] = useState('total'); // 'total' | 'build'
  const [totalPackage, setTotalPackage] = useState(500000);
  const [grossPay, setGrossPay] = useState(415000);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const [benefits, setBenefits] = useState([
    { id: 1, name: 'Food / Meals', amount: 30000, delivery: 'Via company vendor or vouchers' },
    { id: 2, name: 'Transport', amount: 20000, delivery: 'Via company arrangement' },
    { id: 3, name: 'Health Cover (HMO)', amount: 25000, delivery: 'HMO provider' },
    { id: 4, name: 'Life Insurance', amount: 10000, delivery: 'Insurance provider' },
  ]);
  const [customBenefits, setCustomBenefits] = useState([]);

  const [opts, setOpts] = useState({
    basicPct: 0.4, housingPct: 0.2, transportPct: 0.1,
    employerPensionRate: 0.1, employeePensionRate: 0.08,
    annualRent: 1200000,
  });

  // Multiple salary bands
  const [bands, setBands] = useState([]);

  // Headcount
  const [headcount, setHeadcount] = useState(50);

  const allBenefits = [...benefits, ...customBenefits];
  const totalBen = allBenefits.reduce((s, b) => s + b.amount, 0);

  // Compute effective total package based on mode
  const effectiveTotal = useMemo(() => {
    if (mode === 'total') return totalPackage;
    const pctSum = opts.basicPct + opts.housingPct + opts.transportPct;
    const pensionable = pctSum * grossPay;
    const empPension = opts.employerPensionRate * pensionable;
    return grossPay + totalBen + empPension;
  }, [mode, totalPackage, grossPay, totalBen, opts]);

  // Validation
  const maxBenefits = effectiveTotal * 0.8; // can't exceed 80% of total
  const overBudget = mode === 'total' && totalBen > (totalPackage - totalPackage * opts.employerPensionRate * (opts.basicPct + opts.housingPct + opts.transportPct) * 0.5);

  const result = useMemo(() => computeScenario(effectiveTotal, allBenefits, opts), [effectiveTotal, allBenefits, opts]);

  const benefitsSummary = allBenefits.filter(b => b.amount > 0).map(b => `${b.name} ${fmt(b.amount)}`).join(', ');

  const updateBenefit = (id, field, val) => {
    setBenefits(prev => prev.map(b => b.id === id ? { ...b, [field]: val } : b));
  };
  const updateCustom = (id, field, val) => {
    setCustomBenefits(prev => prev.map(b => b.id === id ? { ...b, [field]: val } : b));
  };
  const addCustom = () => {
    setCustomBenefits(prev => [...prev, { id: Date.now(), name: '', amount: 0, delivery: 'Provided through vendor' }]);
  };
  const removeCustom = (id) => {
    setCustomBenefits(prev => prev.filter(b => b.id !== id));
  };

  // Band management
  const addBand = () => {
    setBands(prev => [...prev, { id: Date.now(), total: 350000, benefits: 60000, headcount: 30 }]);
  };
  const updateBand = (id, field, val) => {
    setBands(prev => prev.map(b => b.id === id ? { ...b, [field]: val } : b));
  };
  const removeBand = (id) => {
    setBands(prev => prev.filter(b => b.id !== id));
  };

  // Company-wide summary with bands
  const bandResults = bands.map(band => ({
    ...band,
    result: computeScenario(band.total, [{ name: 'Benefits', amount: band.benefits }], opts),
  }));
  const totalHeadcount = headcount + bands.reduce((s, b) => s + b.headcount, 0);
  const totalMonthlySpend = headcount * effectiveTotal + bands.reduce((s, b) => s + b.headcount * b.total, 0);
  const totalMonthlyBenefits = headcount * totalBen + bands.reduce((s, b) => s + b.headcount * b.benefits, 0);
  const totalAnnualBenefits = totalMonthlyBenefits * 12;

  return (
    <section className="py-20 lg:py-24" id="estimator-section" data-testid="employer-calculator-section">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>Model it for your team.</h2>
          <p className="text-base text-[#6b7280] mt-4 max-w-2xl mx-auto">
            Enter your numbers and see the full side-by-side breakdown — what your team takes home, what you provide through vendors, and what it costs you.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden" data-testid="calculator-container">
          {/* Mode Switcher */}
          <div className="bg-[#fafafa] border-b border-[#e5e7eb] px-6 py-4">
            <p className="text-xs font-bold text-[#374151] uppercase tracking-wider mb-3">How do you want to enter it?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setMode('total')}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${mode === 'total' ? 'bg-[#1a1d1f] text-white' : 'bg-white border border-[#e5e7eb] text-[#6b7280] hover:border-[#d1d5db]'}`}
                data-testid="mode-total"
              >
                I know the total package per employee
              </button>
              <button
                onClick={() => setMode('build')}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${mode === 'build' ? 'bg-[#1a1d1f] text-white' : 'bg-white border border-[#e5e7eb] text-[#6b7280] hover:border-[#d1d5db]'}`}
                data-testid="mode-build"
              >
                I want to start from gross pay and add benefits
              </button>
            </div>
          </div>

          <div className="p-6 lg:p-8">
            {/* Primary Input */}
            {mode === 'total' ? (
              <NumberInput
                value={totalPackage}
                onChange={setTotalPackage}
                label="Total Monthly Package Per Employee"
                sub="This is everything your company spends on this employee — salary, benefits, pension, all of it."
                testId="input-total-package"
              />
            ) : (
              <NumberInput
                value={grossPay}
                onChange={setGrossPay}
                label="Monthly Gross Pay"
                sub="The cash salary portion before deductions. Benefits below are on top of this."
                testId="input-gross-pay"
              />
            )}

            {/* Benefits Inputs */}
            <div className="mt-8">
              <p className="text-sm font-bold text-[#1a1d1f] mb-1" style={{ fontFamily: 'Manrope' }}>Benefits you provide through vendors</p>
              <p className="text-xs text-[#9ca3af] mb-4">These are delivered to your team through approved vendors and providers — not deposited as cash.</p>

              <div className="space-y-2.5">
                {benefits.map(b => (
                  <div key={b.id} className="flex items-center gap-3" data-testid={`benefit-row-${b.id}`}>
                    <span className="text-sm text-[#374151] w-40 flex-shrink-0">{b.name}</span>
                    <div className="relative flex-1 max-w-[160px]">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#9ca3af]">₦</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={b.amount === 0 ? '' : b.amount.toLocaleString()}
                        onChange={e => updateBenefit(b.id, 'amount', parseInt(e.target.value.replace(/\D/g, '') || '0', 10))}
                        className="w-full h-9 pl-7 pr-3 rounded-lg border border-[#e5e7eb] text-sm font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/20 focus:border-[#D64D7A]"
                        style={mono}
                        data-testid={`benefit-amount-${b.id}`}
                      />
                    </div>
                    <span className="text-xs text-[#9ca3af] hidden sm:inline">{b.delivery}</span>
                  </div>
                ))}

                {customBenefits.map(b => (
                  <div key={b.id} className="flex items-center gap-3" data-testid={`custom-benefit-${b.id}`}>
                    <input
                      type="text"
                      value={b.name}
                      onChange={e => updateCustom(b.id, 'name', e.target.value)}
                      placeholder="Benefit name"
                      className="w-40 flex-shrink-0 h-9 px-3 rounded-lg border border-[#e5e7eb] text-sm text-[#1a1d1f] placeholder:text-[#c4c8cc] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/20 focus:border-[#D64D7A]"
                    />
                    <div className="relative flex-1 max-w-[160px]">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#9ca3af]">₦</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={b.amount === 0 ? '' : b.amount.toLocaleString()}
                        onChange={e => updateCustom(b.id, 'amount', parseInt(e.target.value.replace(/\D/g, '') || '0', 10))}
                        className="w-full h-9 pl-7 pr-3 rounded-lg border border-[#e5e7eb] text-sm font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/20 focus:border-[#D64D7A]"
                        style={mono}
                      />
                    </div>
                    <select
                      value={b.delivery}
                      onChange={e => updateCustom(b.id, 'delivery', e.target.value)}
                      className="h-9 px-2 rounded-lg border border-[#e5e7eb] text-xs text-[#6b7280] focus:outline-none hidden sm:block"
                    >
                      <option>Provided through vendor</option>
                      <option>Provided directly by company</option>
                      <option>Voucher / card</option>
                    </select>
                    <button onClick={() => removeCustom(b.id)} className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#9ca3af] hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-4">
                <button onClick={addCustom} className="flex items-center gap-1.5 text-sm font-semibold text-[#D64D7A] hover:underline" data-testid="add-benefit-btn">
                  <Plus className="w-4 h-4" /> Add another benefit
                </button>
                <p className="text-sm font-bold text-[#1a1d1f]" style={mono} data-testid="total-benefits">Total: {fmt(totalBen)}</p>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="mt-6 border-t border-[#f0f0f0] pt-4">
              <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-2 text-xs font-semibold text-[#6b7280] hover:text-[#374151] transition-colors" data-testid="advanced-toggle">
                <Settings className="w-3.5 h-3.5" />
                Customise assumptions
                {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {showAdvanced && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 bg-[#fafafa] rounded-xl p-4 animate-in fade-in" data-testid="advanced-panel">
                  {[
                    { label: 'Basic Salary %', val: opts.basicPct * 100, key: 'basicPct', suffix: '%' },
                    { label: 'Housing %', val: opts.housingPct * 100, key: 'housingPct', suffix: '%' },
                    { label: 'Transport %', val: opts.transportPct * 100, key: 'transportPct', suffix: '%' },
                    { label: 'Employer Pension %', val: opts.employerPensionRate * 100, key: 'employerPensionRate', suffix: '%' },
                    { label: 'Employee Pension %', val: opts.employeePensionRate * 100, key: 'employeePensionRate', suffix: '%' },
                  ].map(field => (
                    <div key={field.key}>
                      <label className="text-[11px] font-semibold text-[#6b7280] mb-1 block">{field.label}</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={field.val}
                          onChange={e => setOpts(prev => ({ ...prev, [field.key]: (parseFloat(e.target.value) || 0) / 100 }))}
                          className="w-full h-9 px-3 pr-7 rounded-lg border border-[#e5e7eb] text-sm font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/20"
                          style={mono}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#9ca3af]">%</span>
                      </div>
                    </div>
                  ))}
                  <div>
                    <label className="text-[11px] font-semibold text-[#6b7280] mb-1 block">Annual Rent (₦)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={opts.annualRent.toLocaleString()}
                      onChange={e => setOpts(prev => ({ ...prev, annualRent: parseInt(e.target.value.replace(/\D/g, '') || '0', 10) }))}
                      className="w-full h-9 px-3 rounded-lg border border-[#e5e7eb] text-sm font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/20"
                      style={mono}
                    />
                  </div>
                </div>
              )}
              {showAdvanced && <p className="text-[11px] text-[#9ca3af] mt-2">Most companies can leave these at defaults. Adjust if you know your exact breakdown.</p>}
            </div>
          </div>

          {/* === LIVE OUTPUT === */}
          <div className="border-t border-[#e5e7eb] bg-[#fafafa] px-6 lg:px-8 py-8">
            {/* Quick View */}
            <p className="text-xs font-bold text-[#1a1d1f] uppercase tracking-wider mb-1" style={{ fontFamily: 'Manrope' }}>
              For a {fmt(effectiveTotal)}/month employee:
            </p>
            <p className="text-xs font-bold text-[#9ca3af] uppercase tracking-wider mb-3">Quick view:</p>

            <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden" data-testid="live-quick-view">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e5e7eb]">
                    <th className="text-left px-5 py-2.5 text-xs font-semibold text-[#9ca3af] uppercase tracking-wider"></th>
                    <th className="text-right px-5 py-2.5 text-xs font-semibold text-[#9ca3af] uppercase tracking-wider">Without Pebbles</th>
                    <th className="text-right px-5 py-2.5 text-xs font-semibold text-[#00B894] uppercase tracking-wider">With Pebbles</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#f0f0f0]">
                    <td className="px-5 py-2.5 text-[#374151]">Your Spend</td>
                    <td className="px-5 py-2.5 text-right text-[#6b7280]" style={mono}>{fmt(effectiveTotal)}</td>
                    <td className="px-5 py-2.5 text-right text-[#374151]" style={mono}>{fmt(effectiveTotal)}</td>
                  </tr>
                  <tr className="border-b border-[#f0f0f0]">
                    <td className="px-5 py-2.5 text-[#374151]">Benefits You Provide</td>
                    <td className="px-5 py-2.5 text-right text-[#6b7280]" style={mono}>₦0</td>
                    <td className="px-5 py-2.5 text-right text-[#374151]" style={mono}>{fmt(totalBen)}</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-5 py-2.5 font-bold text-[#1a1d1f]">What They Have Left</td>
                    <td className="px-5 py-2.5 text-right font-bold text-[#374151]" style={mono}>~{fmt(result.leftWithout)}</td>
                    <td className="px-5 py-2.5 text-right font-bold text-[#00B894]" style={mono}>~{fmt(result.leftWith)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Delta */}
            {result.delta > 0 ? (
              <div className="bg-[#00B894]/8 rounded-xl p-3.5 text-center mt-4" data-testid="delta-banner">
                <p className="text-sm font-bold text-[#1a1d1f]">Your team has {fmt(result.delta)} more every month. Your spend doesn't change.</p>
              </div>
            ) : totalBen === 0 ? (
              <div className="bg-[#f9fafb] border border-[#e5e7eb] rounded-xl p-3.5 text-center mt-4" data-testid="delta-empty">
                <p className="text-sm text-[#9ca3af]">Add benefits above to see the difference.</p>
              </div>
            ) : null}

            {/* Full Breakdown Toggle */}
            <button onClick={() => setShowBreakdown(!showBreakdown)} className="flex items-center gap-2 text-sm font-semibold text-[#D64D7A] hover:underline mt-5" data-testid="expand-breakdown-btn">
              {showBreakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {showBreakdown ? 'Hide full breakdown' : 'Show full breakdown'}
            </button>

            {showBreakdown && (
              <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden mt-3 animate-in fade-in" data-testid="full-breakdown-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e5e7eb] bg-[#fafafa]">
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-[#9ca3af] uppercase tracking-wider w-[45%]"></th>
                      <th className="text-right px-5 py-2.5 text-xs font-semibold text-[#9ca3af] uppercase tracking-wider">Without Pebbles</th>
                      <th className="text-right px-5 py-2.5 text-xs font-semibold text-[#00B894] uppercase tracking-wider">With Pebbles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Your Spend Per Employee', w: fmt(effectiveTotal), p: fmt(effectiveTotal), subW: '(all cash)', subP: '(pay + benefits)' },
                      { label: `Employer Pension (${(opts.employerPensionRate * 100).toFixed(0)}%)`, w: fmt(result.employerPensionWithout), p: fmt(result.employerPensionWith) },
                      { label: 'Benefits You Provide', w: '₦0', p: fmt(totalBen), subP: benefitsSummary ? `(${benefitsSummary})` : undefined },
                      { label: `Employee Pension (${(opts.employeePensionRate * 100).toFixed(0)}%)`, w: `~${fmt(result.employeePensionWithout)}`, p: `~${fmt(result.employeePensionWith)}` },
                      { label: 'Estimated PAYE', w: `~${fmt(result.payeWithout)}`, p: `~${fmt(result.payeWith)}` },
                      { label: 'Their Cash After Tax', w: `~${fmt(result.cashAfterTaxWithout)}`, p: `~${fmt(result.cashAfterTaxWith)}` },
                      { label: 'Living Expenses', w: `-${fmt(totalBen)}`, p: '₦0 (you provide these)', subW: '(from their pocket)', highlight: true },
                      { label: 'What They Have Left', w: `~${fmt(result.leftWithout)}`, p: `~${fmt(result.leftWith)}`, bold: true },
                    ].map((row, i) => (
                      <tr key={i} className={`border-b border-[#f0f0f0] last:border-0 ${row.bold ? 'bg-[#f0fdf4]' : row.highlight ? 'bg-[#fef2f2]/50' : ''}`}>
                        <td className={`px-5 py-2.5 text-[#374151] ${row.bold ? 'font-extrabold text-[#1a1d1f]' : ''}`}>{row.label}</td>
                        <td className="px-5 py-2.5 text-right align-top">
                          <span className={row.bold ? 'font-extrabold text-[#374151]' : 'text-[#6b7280]'} style={mono}>{row.w}</span>
                          {row.subW && <p className="text-[11px] text-[#9ca3af]">{row.subW}</p>}
                        </td>
                        <td className="px-5 py-2.5 text-right align-top">
                          <span className={row.bold ? 'font-extrabold text-[#00B894]' : 'text-[#374151]'} style={mono}>{row.p}</span>
                          {row.subP && <p className="text-[11px] text-[#9ca3af]">{row.subP}</p>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* HEADCOUNT MULTIPLIER */}
            <div className="mt-10 pt-8 border-t border-[#e5e7eb]">
              <p className="text-base font-bold text-[#1a1d1f] mb-4" style={{ fontFamily: 'Manrope' }}>Now multiply by your team.</p>

              <div className="flex items-center gap-3 mb-5">
                <label className="text-sm text-[#374151]">Number of employees at this salary band:</label>
                <input
                  type="number"
                  value={headcount}
                  onChange={e => setHeadcount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-24 h-9 px-3 rounded-lg border border-[#e5e7eb] text-sm font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/20"
                  style={mono}
                  data-testid="headcount-input"
                />
              </div>

              <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden" data-testid="headcount-results">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e5e7eb] bg-[#fafafa]">
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-[#9ca3af] uppercase tracking-wider"></th>
                      <th className="text-right px-5 py-2.5 text-xs font-semibold text-[#9ca3af] uppercase tracking-wider">Without Pebbles</th>
                      <th className="text-right px-5 py-2.5 text-xs font-semibold text-[#00B894] uppercase tracking-wider">With Pebbles</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[#f0f0f0]">
                      <td className="px-5 py-2.5 text-[#374151]">Monthly Spend (total)</td>
                      <td className="px-5 py-2.5 text-right text-[#6b7280]" style={mono}>{fmt(headcount * effectiveTotal)}</td>
                      <td className="px-5 py-2.5 text-right text-[#374151]" style={mono}>{fmt(headcount * effectiveTotal)}</td>
                    </tr>
                    <tr className="border-b border-[#f0f0f0]">
                      <td className="px-5 py-2.5 text-[#374151]">Monthly Benefits Delivered</td>
                      <td className="px-5 py-2.5 text-right text-[#6b7280]" style={mono}>₦0</td>
                      <td className="px-5 py-2.5 text-right text-[#374151]" style={mono}>{fmt(headcount * totalBen)}</td>
                    </tr>
                    <tr className="border-b border-[#f0f0f0]">
                      <td className="px-5 py-2.5 text-[#374151]">What Your Team Has Left (total)</td>
                      <td className="px-5 py-2.5 text-right text-[#6b7280]" style={mono}>~{fmt(headcount * result.leftWithout)}</td>
                      <td className="px-5 py-2.5 text-right text-[#374151]" style={mono}>~{fmt(headcount * result.leftWith)}</td>
                    </tr>
                    <tr className="border-b border-[#f0f0f0] bg-[#f0fdf4]">
                      <td className="px-5 py-2.5 font-bold text-[#1a1d1f]">Annual Benefits Delivered</td>
                      <td className="px-5 py-2.5 text-right font-bold text-[#374151]" style={mono}>₦0</td>
                      <td className="px-5 py-2.5 text-right font-bold text-[#00B894]" style={mono}>{fmt(headcount * totalBen * 12)}</td>
                    </tr>
                    <tr>
                      <td className="px-5 py-2.5 text-[#374151]">Employer Pension (already paid)</td>
                      <td className="px-5 py-2.5 text-right text-[#6b7280]" style={mono}>{fmt(headcount * result.employerPensionWithout)}/mo</td>
                      <td className="px-5 py-2.5 text-right text-[#374151]" style={mono}>{fmt(headcount * result.employerPensionWith)}/mo <span className="text-xs text-[#00B894]">(now visible)</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Multiple Salary Bands */}
              {bands.length > 0 && (
                <div className="mt-6 space-y-3">
                  {bands.map((band, idx) => (
                    <div key={band.id} className="flex items-center gap-3 bg-[#fafafa] rounded-lg p-3 border border-[#e5e7eb]" data-testid={`salary-band-${idx + 2}`}>
                      <span className="text-xs font-bold text-[#9ca3af] w-16">Band {idx + 2}</span>
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-[10px] text-[#9ca3af]">Total Package</label>
                          <input type="text" inputMode="numeric" value={band.total === 0 ? '' : band.total.toLocaleString()} onChange={e => updateBand(band.id, 'total', parseInt(e.target.value.replace(/\D/g, '') || '0', 10))}
                            className="w-full h-8 px-2 rounded border border-[#e5e7eb] text-xs font-semibold" style={mono} />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#9ca3af]">Benefits</label>
                          <input type="text" inputMode="numeric" value={band.benefits === 0 ? '' : band.benefits.toLocaleString()} onChange={e => updateBand(band.id, 'benefits', parseInt(e.target.value.replace(/\D/g, '') || '0', 10))}
                            className="w-full h-8 px-2 rounded border border-[#e5e7eb] text-xs font-semibold" style={mono} />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#9ca3af]">Headcount</label>
                          <input type="number" value={band.headcount} onChange={e => updateBand(band.id, 'headcount', Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-full h-8 px-2 rounded border border-[#e5e7eb] text-xs font-semibold" style={mono} />
                        </div>
                      </div>
                      <button onClick={() => removeBand(band.id)} className="w-7 h-7 rounded hover:bg-red-50 flex items-center justify-center text-[#9ca3af] hover:text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={addBand} className="flex items-center gap-1.5 text-sm font-semibold text-[#6b7280] hover:text-[#374151] mt-4" data-testid="add-band-btn">
                <Plus className="w-4 h-4" /> Add another salary band
              </button>

              {/* Company-wide summary */}
              {bands.length > 0 && (
                <div className="mt-6 bg-[#1a1d1f] rounded-xl p-6" data-testid="company-summary">
                  <p className="text-xs font-bold text-[#9ca3af] uppercase tracking-wider mb-3">Your Company Summary ({totalHeadcount} employees across {bands.length + 1} bands)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[11px] text-[#6b7280]">Total Monthly Spend</p>
                      <p className="text-lg font-bold text-white" style={mono}>{fmt(totalMonthlySpend)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#6b7280]">Monthly Benefits</p>
                      <p className="text-lg font-bold text-[#00B894]" style={mono}>{fmt(totalMonthlyBenefits)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#6b7280]">Annual Benefits</p>
                      <p className="text-lg font-bold text-[#D64D7A]" style={mono}>{fmt(totalAnnualBenefits)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#6b7280]">Total Headcount</p>
                      <p className="text-lg font-bold text-white" style={mono}>{totalHeadcount}</p>
                    </div>
                  </div>
                  <p className="text-sm text-[#d1d5db] mt-4">Same total payroll budget. <span className="text-[#00B894] font-bold">{fmt(totalAnnualBenefits)}</span> in annual benefits your team actually uses.</p>
                </div>
              )}

              {/* Single-band CTA */}
              {bands.length === 0 && (
                <div className="bg-[#1a1d1f] rounded-xl p-5 mt-6 text-center" data-testid="headcount-cta">
                  <p className="text-base font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                    <span className="text-[#00B894]">{fmt(headcount * totalBen * 12)}</span> in annual benefits. Same payroll budget.
                  </p>
                  <button onClick={openDemo} className="inline-flex items-center gap-2 bg-[#D64D7A] hover:bg-[#c4406b] text-white rounded-xl h-10 px-6 text-sm font-semibold mt-3 transition-colors" data-testid="calc-demo-btn">
                    Book a Demo <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {bands.length > 0 && (
                <div className="text-center mt-4">
                  <button onClick={openDemo} className="inline-flex items-center gap-2 bg-[#D64D7A] hover:bg-[#c4406b] text-white rounded-xl h-10 px-6 text-sm font-semibold transition-colors" data-testid="calc-demo-btn-multi">
                    Book a Demo <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-[11px] text-[#9ca3af] mt-4 leading-relaxed max-w-3xl mx-auto">
          This calculator shows illustrative figures based on the inputs you provide. Assumes standard salary breakdown unless customised, NTA 2025 tax bands, and rent relief applied. Actual figures may vary by state PAYE rates, individual reliefs, and employer configuration. Pebbles platform pricing is not included in this comparison. Tax treatment of benefits depends on form of delivery, documentation, and compliance with NTA 2025. Consult a qualified tax professional for your specific situation.
        </p>
      </div>
    </section>
  );
}
